import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLayout } from "../../contexts/LayoutContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useNotifications } from "../../contexts/NotificationContext";
import axiosInstance from "../../utils/axiosInstance";
import { getTenantFromToken } from "../../utils/utils";
import BrandedLoginHeader from "./BrandedLoginHeader";

// ── Icons (inline SVG — no dependency needed) ─────────────────────────────

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);

const BellIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const SunIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// Per-tenant branded text header (company name shown instead of a logo image)
const customBrandedClients: Record<
  string,
  { companyName: string; accentColor?: string }
> = {
  "2bccf661-3b86-4839-884b-921268d0a94e": { companyName: "NBCUniversal" },
  "7563c07c-2572-4cc2-b14b-1d43b6010a78": { companyName: "Jope" },
  "544189b1-c029-4dd6-b651-15207fccfe76": { companyName: "Mightier Inc." },
  "2e332294-8271-4a0e-8391-8b635f86a27e": { companyName: "Universal Ads" },
  "31bb6772-77b5-4142-bcfc-04ff26927b54": { companyName: "Efile" },
  "44a49f83-cfd4-47ef-aca3-87bf19d97494": { companyName: "Restore" },
};

// ── Component ──────────────────────────────────────────────────────────────

export default function SiteHeader() {
  const navigate = useNavigate();
  const { isSidebarExpanded, setSidebarExpanded } = useLayout();
  const { theme, toggleTheme, isDark } = useTheme();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const tenantId = getTenantFromToken() ?? "";

  // Get user initial from token
  const getUserInitial = (): string => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return "U";
      const { jwtDecode } = require("jwt-decode");
      const decoded: any = jwtDecode(token);
      const email = decoded.email || decoded.sub || "";
      return email.charAt(0).toUpperCase() || "U";
    } catch {
      return "U";
    }
  };

  const userInitial = getUserInitial();

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleNotificationClick = (n: { id: string; action_url: string | null }) => {
    markAsRead(n.id);
    setNotifOpen(false);
    if (n.action_url) navigate(n.action_url);
  };

  // Cmd+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("open-command-palette"));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const openCommandPalette = () => {
    window.dispatchEvent(new CustomEvent("open-command-palette"));
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.get("/api/logout");
    } catch {}
    navigate("/auth/login?logout=true");
  };

  const customBranding = customBrandedClients[tenantId];

  return (
    <>
      <style>{`
        .site-header-search:hover { border-color: var(--border-focus) !important; }
        .site-header-search:focus-within { border-color: var(--border-focus) !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
        .site-header-icon-btn:hover { background: var(--bg-hover); }
        .avatar-btn:hover { opacity: 0.88; }
        .dropdown-item:hover { background: var(--bg-hover); }
        @keyframes header-dropdown-in {
          from { opacity: 0; transform: translateY(-6px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .dropdown-animate { animation: header-dropdown-in 0.15s ease; }
      `}</style>

      <header
        style={{
          height: 'var(--topbar-h)',
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border-default)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          gap: '12px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          flexShrink: 0,
        }}
      >
        {/* ── Left: Logo / Sidebar toggle ─────────────────────────────────── */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setSidebarExpanded(!isSidebarExpanded)}
            className="site-header-icon-btn rounded-md p-1.5 transition-colors duration-150"
            style={{ color: 'var(--text-secondary)' }}
            title="Toggle sidebar"
          >
            <MenuIcon />
          </button>

          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 select-none"
          >
            {customBranding ? (
              <div className="ml-10">
                <BrandedLoginHeader
                  companyName={customBranding.companyName}
                  accentColor={customBranding.accentColor}
                />
              </div>
            ) : (
              <>
                {/* Adrsta logomark */}
                <div
                  style={{
                    width: 28, height: 28,
                    background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                    borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: '-0.5px', fontFamily: 'var(--font-sans)' }}>A</span>
                </div>
                {/* Wordmark */}
                <span style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 15,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.3px',
                  lineHeight: 1,
                }}>
                  Adrsta
                </span>
              </>
            )}
          </button>
        </div>

        {/* ── Center: Search / Command Palette trigger ──────────────────────── */}
        <div className="flex-1 flex justify-center">
          <button
            onClick={openCommandPalette}
            className="site-header-search flex items-center gap-2 w-full transition-all duration-150"
            style={{
              maxWidth: 480,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              padding: '6px 12px',
              color: 'var(--text-tertiary)',
              fontSize: 13,
              fontFamily: 'var(--font-sans)',
              cursor: 'text',
              textAlign: 'left',
            }}
          >
            <SearchIcon />
            <span className="flex-1">Ask anything or search...</span>
            <kbd
              style={{
                padding: '2px 6px',
                background: 'var(--bg-hover)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-tertiary)',
                letterSpacing: '0.03em',
                lineHeight: 1.5,
              }}
            >
              ⌘K
            </kbd>
          </button>
        </div>

        {/* ── Right: Actions ──────────────────────────────────────────────── */}
        <div className="flex items-center gap-1 flex-shrink-0">

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="site-header-icon-btn rounded-md p-1.5 transition-colors duration-150"
            style={{ color: 'var(--text-secondary)' }}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Notification bell */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setNotifOpen(v => !v)}
              className="site-header-icon-btn rounded-md p-1.5 transition-colors duration-150 relative"
              style={{ color: 'var(--text-secondary)' }}
              title="Notifications"
            >
              <BellIcon />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute', top: -2, right: -4,
                    minWidth: 15, height: 15, padding: '0 3px',
                    background: 'var(--error)',
                    borderRadius: 8,
                    border: '1.5px solid var(--bg-surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9.5, fontWeight: 700, color: '#fff',
                    lineHeight: 1, fontFamily: 'var(--font-sans)',
                  }}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div
                className="dropdown-animate absolute right-0 mt-1"
                style={{
                  width: 340,
                  maxHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  zIndex: 200,
                  overflow: 'hidden',
                  top: '100%',
                }}
              >
                <div
                  className="flex items-center justify-between"
                  style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)' }}>
                    Notifications
                  </span>
                  {notifications.length > 0 && (
                    <button
                      onClick={markAllAsRead}
                      style={{ fontSize: 11.5, color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {notifications.length === 0 ? (
                    <div style={{ padding: '28px 14px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12.5, fontFamily: 'var(--font-sans)' }}>
                      You're all caught up
                    </div>
                  ) : (
                    notifications.map(n => (
                      <button
                        key={n.id}
                        onClick={() => handleNotificationClick(n)}
                        className="dropdown-item w-full text-left"
                        style={{
                          display: 'block',
                          padding: '10px 14px',
                          borderBottom: '1px solid var(--border-subtle)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: n.notification_type === 'job_failed' ? 'var(--error)' : 'var(--text-primary)', fontFamily: 'var(--font-sans)' }}>
                          {n.title}
                        </div>
                        {n.message && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2, fontFamily: 'var(--font-sans)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {n.message}
                          </div>
                        )}
                        <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 3, fontFamily: 'var(--font-sans)' }}>
                          {new Date(n.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User avatar + dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(v => !v)}
              className="avatar-btn flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-opacity duration-150"
            >
              <div
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 600, color: '#fff',
                  fontFamily: 'var(--font-sans)',
                  flexShrink: 0,
                }}
              >
                {userInitial}
              </div>
              <span style={{ color: 'var(--text-secondary)' }}>
                <ChevronDownIcon />
              </span>
            </button>

            {/* Dropdown menu */}
            {dropdownOpen && (
              <div
                className="dropdown-animate absolute right-0 mt-1"
                style={{
                  minWidth: 180,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  zIndex: 200,
                  overflow: 'hidden',
                  top: '100%',
                }}
              >
                {[
                  { icon: <UserIcon />,     label: 'Profile',  action: () => { navigate('/settings'); setDropdownOpen(false); } },
                  { icon: <SettingsIcon />, label: 'Settings', action: () => { navigate('/settings'); setDropdownOpen(false); } },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="dropdown-item w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors duration-100"
                    style={{ color: 'var(--text-primary)', fontSize: 13, fontFamily: 'var(--font-sans)' }}
                  >
                    <span style={{ color: 'var(--text-secondary)' }}>{item.icon}</span>
                    {item.label}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '4px 0' }} />
                <button
                  onClick={handleLogout}
                  className="dropdown-item w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors duration-100"
                  style={{ color: 'var(--error)', fontSize: 13, fontFamily: 'var(--font-sans)' }}
                >
                  <LogoutIcon />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
